import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import {
  WIKI_ARTICLE_KIND,
  articleTitle,
  getTagValue,
  isValidWikiArticle,
  parseAddressPointer,
} from '@/lib/nip54';
import {
  PUBLICATION_CONTENT_KIND,
  PUBLICATION_INDEX_KIND,
  isValidPublicationIndex,
  publicationTitle,
  sectionTitle,
} from '@/lib/nkbip01';
import { isLikeReaction } from '@/lib/nip25';
import { isValidHighlight } from '@/lib/nip84';

/**
 * Kinds from the wiki/publication ecosystem that stars and highlights can
 * point at. A reaction or highlight whose target isn't one of these kinds
 * doesn't belong in the activity feed (e.g. a highlight of a web URL or a
 * kind-1 note from another client).
 */
export const ECOSYSTEM_TARGET_KINDS = [WIKI_ARTICLE_KIND, PUBLICATION_INDEX_KIND, PUBLICATION_CONTENT_KIND];

export type ActivityType = 'article' | 'article-update' | 'fork' | 'publication' | 'star' | 'highlight';

export interface ActivityTarget {
  kind: number;
  /** Author of the targeted content, when known (from the `a` tag or the resolved event). */
  pubkey?: string;
  /** `d` identifier of the target, when known. */
  d?: string;
  /** Resolved display title — undefined when the target event couldn't be fetched. */
  title?: string;
  /**
   * Canonical in-app link. Undefined for publication sections: a bare
   * section can't be linked without its parent index's `d` tag, which a
   * star/highlight doesn't carry.
   */
  href?: string;
}

export interface ActivityItem {
  /** Stable React key — the underlying action event's id. */
  id: string;
  type: ActivityType;
  /** The action event itself (the article/publication/reaction/highlight). */
  event: NostrEvent;
  createdAt: number;
  /** For stars and highlights: what was starred/highlighted. */
  target?: ActivityTarget;
  /** For forks: the author of the version that was forked from, when known. */
  forkSourcePubkey?: string;
}

/** One page of raw feed data as fetched by `useActivityFeed`. */
export interface ActivityPage {
  /** All action events fetched (articles, publication indices, reactions, highlights). */
  events: NostrEvent[];
  /** Ids of action events retracted via a same-author NIP-09 deletion. */
  deletedIds: string[];
  /** Resolved star/highlight target events (for titles and links). */
  targets: NostrEvent[];
}

/**
 * Returns the fork-source author's pubkey when this article event carries a
 * fork marker (Wikistar writes `e`/`a` tags with the `fork` marker when an
 * author forks someone else's version — see `ArticleEditPage`), `''` when
 * it's a fork whose source is only known by event id, or `undefined` when
 * the event isn't marked as a fork at all.
 */
export function getForkSource(event: NostrEvent): string | undefined {
  let isFork = false;
  for (const tag of event.tags) {
    if (tag[3] !== 'fork') continue;
    if (tag[0] === 'a') {
      const pointer = parseAddressPointer(tag[1] ?? '');
      if (pointer) return pointer.pubkey;
    }
    if (tag[0] === 'e') isFork = true;
  }
  return isFork ? '' : undefined;
}

interface RawTarget {
  kind?: number;
  pubkey?: string;
  d?: string;
  id?: string;
}

/**
 * Extracts what a reaction/highlight points at: prefers the `a` tag
 * coordinate (always present for Wikistar-authored actions on addressable
 * content), falls back to the bare `e` tag id. For reactions, NIP-25's `k`
 * tag supplies the target kind when there's no usable `a` tag.
 */
export function getActionTarget(event: NostrEvent): RawTarget | undefined {
  const aTag = event.tags.find(([n]) => n === 'a')?.[1];
  const eTag = event.tags.find(([n]) => n === 'e')?.[1];

  if (aTag) {
    const pointer = parseAddressPointer(aTag);
    if (pointer) {
      return { kind: pointer.kind, pubkey: pointer.pubkey, d: pointer.identifier, id: eTag };
    }
  }
  if (!eTag) return undefined;

  const kTag = Number(getTagValue(event, 'k'));
  return { id: eTag, kind: Number.isFinite(kTag) && kTag > 0 ? kTag : undefined };
}

/** A stable dedupe key for a raw target — one star per (reactor, target). */
function rawTargetKey(raw: RawTarget): string | undefined {
  if (raw.kind !== undefined && raw.pubkey && raw.d !== undefined) {
    return `${raw.kind}:${raw.pubkey}:${raw.d}`;
  }
  return raw.id;
}

/**
 * Resolves a raw target against fetched target events into a displayable
 * `ActivityTarget`. Returns `undefined` when the target provably isn't part
 * of the wiki ecosystem (e.g. a highlight of a kind-1 note). An unresolved
 * but plausible target (right kind, event not found on relays) is kept with
 * a generic label rather than dropped.
 */
function resolveTarget(
  raw: RawTarget,
  byAddress: Map<string, NostrEvent>,
  byId: Map<string, NostrEvent>,
): ActivityTarget | undefined {
  let event: NostrEvent | undefined;
  if (raw.kind !== undefined && raw.pubkey && raw.d !== undefined) {
    event = byAddress.get(`${raw.kind}:${raw.pubkey}:${raw.d}`);
  }
  if (!event && raw.id) event = byId.get(raw.id);

  const kind = raw.kind ?? event?.kind;
  if (!kind || !ECOSYSTEM_TARGET_KINDS.includes(kind)) return undefined;

  const pubkey = raw.pubkey ?? event?.pubkey;
  const d = raw.d ?? (event ? getTagValue(event, 'd') : undefined);

  let title: string | undefined;
  let href: string | undefined;
  if (event) {
    if (kind === WIKI_ARTICLE_KIND && isValidWikiArticle(event)) {
      title = articleTitle(event);
      if (d && pubkey) href = `/w/${encodeURIComponent(d)}/${nip19.npubEncode(pubkey)}`;
    } else if (kind === PUBLICATION_INDEX_KIND && isValidPublicationIndex(event)) {
      title = publicationTitle(event);
      if (d && pubkey) href = `/p/${encodeURIComponent(d)}/${nip19.npubEncode(pubkey)}`;
    } else if (kind === PUBLICATION_CONTENT_KIND) {
      title = sectionTitle(event);
      // No href: a bare section has no publication context to link to.
    }
  }

  return { kind, pubkey, d, id: event?.id ?? raw.id, title, href };
}

/**
 * Merges raw fetched pages into a single chronological feed:
 *
 *  - **Articles** are deduped per (author, `d`) to their newest edit, then
 *    classified as a fork (fork marker), an update (an earlier edit was seen
 *    in the fetched window), or a fresh article.
 *  - **Publication indices** are deduped per (author, `d`) likewise.
 *  - **Stars** honor NIP-09 unstars, and are deduped per (reactor, target) —
 *    re-starring the same thing is one feed entry, not several.
 *  - **Highlights** honor NIP-09 retractions; each highlight stays its own
 *    entry (a reader may highlight several passages of one article).
 *  - Stars/highlights pointing outside the ecosystem (other clients' kind-1
 *    notes, web URLs) are dropped.
 */
export function mergeActivity(pages: ActivityPage[]): ActivityItem[] {
  const deleted = new Set(pages.flatMap((p) => p.deletedIds));

  const byAddress = new Map<string, NostrEvent>();
  const byId = new Map<string, NostrEvent>();
  for (const page of pages) {
    for (const target of page.targets) {
      const existing = byId.get(target.id);
      if (!existing || target.created_at > existing.created_at) byId.set(target.id, target);
      const d = getTagValue(target, 'd');
      if (d) {
        const key = `${target.kind}:${target.pubkey}:${d}`;
        const prev = byAddress.get(key);
        if (!prev || target.created_at > prev.created_at) byAddress.set(key, target);
      }
    }
  }

  // Dedupe all action events by id — overlapping pages (the `until` window
  // shifts per kind) can return the same event twice.
  const seen = new Set<string>();
  const events = pages
    .flatMap((p) => p.events)
    .filter((e) => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    });

  const items: ActivityItem[] = [];

  // Articles — dedupe per (author, d), keeping the newest edit.
  const articlesByKey = new Map<string, NostrEvent>();
  const articleEditsByKey = new Map<string, number>();
  for (const event of events) {
    if (event.kind !== WIKI_ARTICLE_KIND || !isValidWikiArticle(event)) continue;
    const key = `${event.pubkey}:${getTagValue(event, 'd') ?? ''}`;
    articleEditsByKey.set(key, (articleEditsByKey.get(key) ?? 0) + 1);
    const existing = articlesByKey.get(key);
    if (!existing || event.created_at > existing.created_at) articlesByKey.set(key, event);
  }
  for (const [key, event] of articlesByKey) {
    const forkSource = getForkSource(event);
    items.push({
      id: event.id,
      type: forkSource !== undefined ? 'fork' : (articleEditsByKey.get(key) ?? 0) > 1 ? 'article-update' : 'article',
      event,
      createdAt: event.created_at,
      forkSourcePubkey: forkSource || undefined,
    });
  }

  // Publication indices — dedupe per (author, d), keeping the newest.
  const publicationsByKey = new Map<string, NostrEvent>();
  for (const event of events) {
    if (event.kind !== PUBLICATION_INDEX_KIND || !isValidPublicationIndex(event)) continue;
    const key = `${event.pubkey}:${getTagValue(event, 'd') ?? ''}`;
    const existing = publicationsByKey.get(key);
    if (!existing || event.created_at > existing.created_at) publicationsByKey.set(key, event);
  }
  for (const event of publicationsByKey.values()) {
    items.push({ id: event.id, type: 'publication', event, createdAt: event.created_at });
  }

  // Stars — like reactions, minus unstars, one per (reactor, target).
  const starsByKey = new Map<string, NostrEvent>();
  for (const event of events) {
    if (!isLikeReaction(event) || deleted.has(event.id)) continue;
    const raw = getActionTarget(event);
    const targetKey = raw && rawTargetKey(raw);
    if (!targetKey) continue;
    const key = `${event.pubkey}:${targetKey}`;
    const existing = starsByKey.get(key);
    if (!existing || event.created_at > existing.created_at) starsByKey.set(key, event);
  }
  for (const event of starsByKey.values()) {
    const raw = getActionTarget(event);
    if (!raw) continue;
    const target = resolveTarget(raw, byAddress, byId);
    if (!target) continue;
    items.push({ id: event.id, type: 'star', event, createdAt: event.created_at, target });
  }

  // Highlights — valid ones, minus retractions.
  for (const event of events) {
    if (!isValidHighlight(event) || deleted.has(event.id)) continue;
    const raw = getActionTarget(event);
    if (!raw) continue;
    const target = resolveTarget(raw, byAddress, byId);
    if (!target) continue;
    items.push({ id: event.id, type: 'highlight', event, createdAt: event.created_at, target });
  }

  items.sort((a, b) => b.createdAt - a.createdAt);
  return items;
}

/** Generic fallback label for a target whose event couldn't be resolved. */
export function targetKindLabel(kind: number): string {
  switch (kind) {
    case WIKI_ARTICLE_KIND:
      return 'an article';
    case PUBLICATION_INDEX_KIND:
      return 'a publication';
    case PUBLICATION_CONTENT_KIND:
      return 'a section';
    default:
      return 'something';
  }
}
